import {
  COACH_ADMIN_PROFILE_ROLE,
  isPermissionDrivenRole,
  isPermissionGranted,
  permissionValue,
  type PermissionAction,
  type PermissionGroupId,
  type ProfilePermissions,
  type ProfileRoleType,
} from '@strength/domain';

export function canAccessPermissions(
  role: ProfileRoleType,
  customPermissionsEnabled: boolean,
) {
  return customPermissionsEnabled && role === COACH_ADMIN_PROFILE_ROLE;
}

/**
 * Whether the role may open a page gated by a single View key. Only
 * permission-driven roles consult the document (absent means granted); every
 * legacy role passes — no query, no document. Includes the tablet-only kiosk
 * role, whose sign-in flow must never consult it (SC-4378).
 */
export function canAccessView(
  role: ProfileRoleType,
  permissions: ProfilePermissions | null | undefined,
  group: PermissionGroupId,
  action: PermissionAction,
) {
  if (!isPermissionDrivenRole(role)) return true;
  return isPermissionGranted(
    permissionValue(permissions ?? null, group, action),
  );
}

/**
 * Route-guard decision for a View-gated page: 'allow' mounts the page,
 * 'redirect' bounces to the app index. The permissions fetch is injected so
 * the caller supplies the live tRPC query. A failed fetch FAILS CLOSED — a
 * fetch error must never read as a granted document (for /actuals/tablet,
 * the TeamCoach_TabletAthlete sync-group governance does not cover the
 * tablet.weightUnits / tablet.addExerciseNote procedures — SC-4378).
 */
export async function viewAccessDecision(
  role: ProfileRoleType,
  group: PermissionGroupId,
  fetchPermissions: () => Promise<ProfilePermissions | null | undefined>,
  action: PermissionAction = 'view',
): Promise<'allow' | 'redirect'> {
  // Legacy roles always pass — no query, no document.
  if (!isPermissionDrivenRole(role)) return 'allow';
  let permissions: ProfilePermissions | null | undefined;
  try {
    permissions = await fetchPermissions();
  } catch {
    return 'redirect';
  }
  return canAccessView(role, permissions, group, action) ? 'allow' : 'redirect';
}

/** One gated tab, shaped like the entries in team-tabs.ts / library-tabs.ts. */
export interface PermissionTab {
  /** Full tab path, e.g. '/$teamId/team/profiles'. */
  to: string;
  /** Full dotted key, e.g. 'teams.profiles.view'. */
  permissionAccess: string;
}

export type TabAreaDecision =
  | 'allow'
  | { kind: 'tab'; to: string }
  | { kind: 'plan' };

/**
 * Route-guard decision for one tab of a tabbed area (Teams, Library):
 * 'allow' mounts the tab; a hidden tab redirects to the first visible tab
 * in the area; with no visible tab at all it punts to the plan page. The
 * caller passes the area's tab list — the SAME source of truth the nav
 * links render from — so the guard and the links cannot disagree
 * (SC-4792). The permissions fetch is injected so the caller supplies the
 * cached tRPC query. A failed fetch FAILS CLOSED to the plan page — a
 * fetch error must never read as a granted tab.
 */
export async function tabAccessDecision(
  role: ProfileRoleType,
  tabTo: string,
  tabs: readonly PermissionTab[],
  fetchPermissions: () => Promise<ProfilePermissions | null | undefined>,
): Promise<TabAreaDecision> {
  // Legacy roles always pass — no query, no document.
  if (!isPermissionDrivenRole(role)) return 'allow';
  let permissions: ProfilePermissions | null | undefined;
  try {
    permissions = await fetchPermissions();
  } catch {
    return { kind: 'plan' };
  }
  const visible = tabs.filter(({ permissionAccess }) => {
    const lastDot = permissionAccess.lastIndexOf('.');
    return canAccessView(
      role,
      permissions,
      permissionAccess.slice(0, lastDot) as PermissionGroupId,
      permissionAccess.slice(lastDot + 1) as PermissionAction,
    );
  });
  if (visible.some((tab) => tab.to === tabTo)) return 'allow';
  const first = visible[0];
  return first ? { kind: 'tab', to: first.to } : { kind: 'plan' };
}
